import { useState } from "react";
import { useDashboardData } from "hooks/use-dashboard-data";
import { usePaths } from "BEDROCK/paths";
import { motion } from "framer-motion";
import styles from "./dashboard-1.module.css";

const formatCurrency = (amountInPence: number): string => {
  return `£${(amountInPence / 100).toFixed(2)}`;
};

export const Dashboard1 = () => {
  const [timeRange, setTimeRange] = useState(30);
  const paths = usePaths();

  const { data, isLoading, isError, refetch } = useDashboardData(timeRange);

  const getTimeRangeLabel = (days: number): string => {
    switch (days) {
      case 1:
        return "last day";
      case 7:
        return "last week";
      case 30:
        return "last month";
      case 90:
        return "last 90 days";
      default:
        return `last ${days} days`;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading dashboard...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Error loading dashboard data. Please try again.
          <button onClick={() => refetch()} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Dashboard</h1>
        <div className={styles.controls}>
          <div className={styles.timeRangeSelector}>
            <label>Time range:</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className={styles.timeRangeSelect}
            >
              <option value={1}>Last day</option>
              <option value={7}>Last week</option>
              <option value={30}>Last month</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <button onClick={() => refetch()} className={styles.refreshButton}>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.panels}>
        {/* Users Panel */}
        <motion.div
          className={styles.panel}
          onClick={() => paths.navigateTo(paths.users)}
          whileHover={{ y: -4, boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)" }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div className={styles.panelHeader}>
            <h2>Users</h2>
          </div>
          <div className={styles.panelContent}>
            <div className={styles.metric}>
              <div className={styles.metricValue}>{data?.users.total ?? 0}</div>
              <div className={styles.metricLabel}>Total users</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {data?.users.newInPeriod ?? 0}
              </div>
              <div className={styles.metricLabel}>
                Signed up in {getTimeRangeLabel(timeRange)}
              </div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {data?.users.activeInPeriod ?? 0}
              </div>
              <div className={styles.metricLabel}>
                Active in {getTimeRangeLabel(timeRange)}
              </div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                <span className={styles.warning}>{data?.mobileSignups.unconverted ?? 0}</span>
                {" / "}
                <span className={styles.success}>{data?.mobileSignups.converted ?? 0}</span>
              </div>
              <div className={styles.metricLabel}>
                Mobile reminders sent in {getTimeRangeLabel(timeRange)}
                <div className={styles.metricSubLabel}>Unconverted / Converted</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Orders Panel */}
        <motion.div
          className={styles.panel}
          onClick={() => paths.navigateTo(paths.orders)}
          whileHover={{ y: -4, boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)" }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div className={styles.panelHeader}>
            <h2>Orders</h2>
          </div>
          <div className={styles.panelContent}>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {data?.orders.created ?? 0}
              </div>
              <div className={styles.metricLabel}>
                Orders in "created" state
                <div className={styles.metricSubLabel}>Pre-vetting</div>
              </div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {data?.orders.newInPeriod ?? 0}
              </div>
              <div className={styles.metricLabel}>
                Created in {getTimeRangeLabel(timeRange)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stripe Panel */}
        <motion.div
          className={styles.panel}
          whileHover={{ y: -4, boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)" }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div className={styles.panelHeader}>
            <h2>Stripe</h2>
          </div>
          <div className={styles.panelContent}>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {formatCurrency(data?.stripe.revenueInPeriod ?? 0)}
              </div>
              <div className={styles.metricLabel}>
                Revenue in {getTimeRangeLabel(timeRange)}
              </div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {data?.stripe.transactionCount ?? 0}
              </div>
              <div className={styles.metricLabel}>
                Transactions in {getTimeRangeLabel(timeRange)}
              </div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {formatCurrency(data?.stripe.averageOrderValue ?? 0)}
              </div>
              <div className={styles.metricLabel}>Average order value</div>
            </div>
            <div className={styles.metric}>
              <div className={styles.metricValue}>
                {formatCurrency(data?.stripe.currentBalance ?? 0)}
              </div>
              <div className={styles.metricLabel}>Current balance</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
